import type { Recording } from "../../api/types";
import {
  localDay,
  nextLocalDay,
  recordedSeconds,
  recordingDays,
  recordingIntervals,
} from "./recordingTimeline";

export type ActivityBin = { start: number; end: number; seconds: number };

/** All time shares the team's first capture, regardless of the people filter. */
export function activityCoveragePeriod(
  recordings: Recording[],
  from: string,
  to: string,
  now = Date.now(),
) {
  const intervals = recordingIntervals(recordings);
  const start = from
    ? new Date(`${from}T00:00:00`).getTime()
    : intervals.reduce((first, interval) => Math.min(first, interval.start), now);
  const end = Math.min(to ? nextLocalDay(new Date(`${to}T00:00:00`).getTime()) : now, now);
  return { start, end };
}

/** Merge devices per person, but retain each person's contribution to team totals. */
export function activityBins(
  recordings: Recording[],
  from?: string,
  to?: string,
  day?: number,
): ActivityBin[] {
  const intervals = recordingIntervals(recordings, undefined, undefined, true);
  const people = [...new Set(intervals.map(item => item.recording.user_id))].map(id =>
    intervals.filter(item => item.recording.user_id === id),
  );
  const bounds =
    day === undefined
      ? recordingDays(
          intervals,
          from?.length === 10 ? `${from}T00:00:00` : from,
          to?.length === 10 ? `${to}T00:00:00` : to,
        ).map(start => ({ start, end: nextLocalDay(start) }))
      : (() => {
          const result: Array<{ start: number; end: number }> = [];
          // Elapsed-hour boundaries retain both occurrences of a repeated DST hour.
          for (let start = localDay(day); start < nextLocalDay(localDay(day)); start += 3600000)
            result.push({ start, end: Math.min(start + 3600000, nextLocalDay(localDay(day))) });
          return result;
        })();
  return bounds.map(({ start, end }) => ({
    start,
    end,
    seconds: people.reduce((sum, person) => sum + recordedSeconds(person, start, end), 0),
  }));
}

export function activitySeconds(recordings: Recording[], from = -Infinity, to = Infinity) {
  const intervals = recordingIntervals(recordings, undefined, undefined, true);
  return [...new Set(recordings.map(item => item.user_id))].reduce(
    (sum, id) =>
      sum +
      recordedSeconds(
        intervals.filter(item => item.recording.user_id === id),
        from,
        to,
      ),
    0,
  );
}

/** Keep a readable number of bars while preserving every day's captured time. */
export function activityBucketDays(dayCount: number, maxBars = 32) {
  return (
    [1, 2, 3, 7, 14, 30, 90, 365].find(size => Math.ceil(dayCount / size) <= maxBars) ??
    Math.ceil(dayCount / maxBars)
  );
}

export function aggregateActivityBins(bins: ActivityBin[], binsPerBucket: number) {
  const result: ActivityBin[] = [];
  for (let index = 0; index < bins.length; index += binsPerBucket) {
    const group = bins.slice(index, index + binsPerBucket);
    result.push({
      start: group[0].start,
      end: group[group.length - 1].end,
      seconds: group.reduce((sum, bin) => sum + bin.seconds, 0),
    });
  }
  return result;
}

/** Calculate hourly detail only for the visible window, retaining calendar days when zoomed out. */
export function activityHistoryBins(
  recordings: Recording[],
  days: ActivityBin[],
  from: number,
  to: number,
) {
  const visibleDays = days.filter(bin => bin.end > from && bin.start < to);
  const hourly = to - from <= 48 * 3600000;
  const size = hourly
    ? to - from <= 32 * 3600000
      ? 1
      : 2
    : activityBucketDays(visibleDays.length);
  const source = hourly
    ? visibleDays
        .flatMap(day => activityBins(recordings, undefined, undefined, day.start))
        .filter(bin => bin.end > from && bin.start < to)
    : visibleDays;
  const bins = aggregateActivityBins(source, size).map(bin => {
    const start = Math.max(from, bin.start);
    const end = Math.min(to, bin.end);
    return start === bin.start && end === bin.end
      ? bin
      : { start, end, seconds: activitySeconds(recordings, start, end) };
  });
  return { bins, size, hourly };
}
